// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Automation Area
export class AreaAutomation {
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(dataCore) {
        this.dataCore = dataCore;
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Read
    /** Fetches all pipelines that are available by workspace id and design system id */
    async getPipelines(workspaceId, designSystemId) {
        return this.dataCore.pipelines(workspaceId, designSystemId);
    }
    /** Fetches all builds of pipelines
     * @param workspaceId - Identifier of the workspace where the pipeline is installed
     * @param versionId - Identifier of the version where the pipeline is installed
     */
    async getExportBuilds(workspaceId, versionId) {
        return this.dataCore.exportBuilds(workspaceId, versionId);
    }
    /** Fetches a single export build.
     * @param workspaceId - Identifier of the workspace associated with the build.
     * @param buildId - Identifier of the build.
     */
    async getExportBuild(workspaceId, buildId) {
        return this.dataCore.exportBuild(workspaceId, buildId);
    }
    /** Fetches build logs of a specific build
     * @param workspaceId - Identifier of the workspace where the pipeline is installed
     * @param buildId - Identifier of the build to fetch logs for
     */
    async getExportBuildLogs(workspaceId, buildId) {
        return this.dataCore.buildLogs(workspaceId, buildId);
    }
    /** Fetches exporters belonging to workspace by id */
    async getExporters(workspaceId, filter) {
        return this.dataCore.exporters(workspaceId, filter);
    }
    /** Updates exporter
     * @param workspaceId - Identifier of the workspace where the exporter is installed
     * @param exporterId - Identifier of the exporter to update
     * @param payload - Payload to update exporter with
     */
    async updateExporter(workspaceId, exporterId, payload) {
        return this.dataCore.updateExporter(workspaceId, exporterId, payload);
    }
    /** Updates exporter configuration property value
     * @param designSystemId
     * @param exporterId
     * @param propertyKey
     * @param propertyValue
     */
    async updateExporterConfigurationPropertyValue(designSystemId, exporterId, propertyKey, propertyValue) {
        await this.dataCore.updateExporterConfigurationPropertyValue(designSystemId, exporterId, propertyKey, propertyValue);
    }
    /** Deletes exporter configuration property value
     * @param designSystemId
     * @param exporterId
     * @param propertyKey
     */
    async deleteExporterConfigurationPropertyValue(designSystemId, exporterId, propertyKey) {
        await this.dataCore.deleteExporterConfigurationPropertyValue(designSystemId, exporterId, propertyKey);
    }
    /** Deletes exporter
     * @param workspaceId - Identifier of the workspace where the exporter is installed
     * @param exporterId - Identifier of the exporter to delete
     */
    async deleteExporter(workspaceId, exporterId) {
        return this.dataCore.deleteExporter(workspaceId, exporterId);
    }
    /** Pulls latest version of exporter from remote repository
     * @param workspaceId - Identifier of the workspace where the exporter is installed
     * @param exporterId - Identifier of the exporter to pull
     */
    async pullLatestExporter(workspaceId, exporterId) {
        return this.dataCore.pullLatestExporter(workspaceId, exporterId);
    }
    /** Fetches all custom configuration properties that specific exporter provides
     * @param exporterId - Identifier of the exporter to fetch configuration for
     * @param from - Remote version to fetch from
     * @returns All custom blocks
     */
    async getExporterConfiguration(workspaceId, exporterId, designSystemId) {
        return this.dataCore.exporterConfigurationProperties(exporterId, workspaceId, designSystemId);
    }
    /** Fetches all custom variants that specific exporter provides
     * @param workspaceId - Identifier of the workspace where the exporter is installed
     * @param exporterId - Identifier of the exporter to fetch configuration for
     * @returns All custom variants
     */
    async getExporterBlockVariants(workspaceId, exporterId) {
        return this.dataCore.documentationExporterCustomVariants(workspaceId, exporterId);
    }
    /** Gets all import jobs that are currently in progress.
     * @param userId - User id to delete integration for. Can only be invoked on user id that is currently authenticated.
     * @returns Nothing
     */
    async getImportJobs(from) {
        return this.dataCore.getImportJobs(from.designSystemId, from.versionId);
    }
    async getImportJob(from, jobId) {
        return this.dataCore.getImportJob(from.designSystemId, from.versionId, jobId);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Create/Update
    async scheduleImportJobForFigmaSources(from, sourceIds, dryRun = false) {
        return this.dataCore.scheduleImportJobForFigmaSources(from.designSystemId, from.versionId, sourceIds, dryRun);
    }
    /** Create pipeline
     * @param workspaceId string
     * @param payload
     * @returns Pipeline
     */
    async createPipeline(workspaceId, payload) {
        return this.dataCore.createPipeline(workspaceId, payload);
    }
    /** Update pipeline
     * @returns Pipeline
     * @param designSystemId
     * @param pipelineId
     * @param payload
     */
    async updatePipeline(designSystemId, pipelineId, payload) {
        return this.dataCore.updatePipeline(designSystemId, pipelineId, payload);
    }
    /**
     * Run pipeline
     * @param workspaceId string
     * @param designSystemVersionId string
     * @param pipelineId string
     */
    async runPipeline(workspaceId, designSystemVersionId, pipelineId) {
        return this.dataCore.runPipeline(workspaceId, designSystemVersionId, pipelineId);
    }
    /** Create exporter
     * @param workspaceId string
     * @param payload NewExporterPayload
     * @returns Exporter
     */
    async createExporter(workspaceId, payload) {
        return this.dataCore.createExporter(workspaceId, payload);
    }
    /** Create exporter build
     * @param workspaceId string
     * @param designSystemId string
     * @param versionId string
     * @param exporterId string
     * @param destinations
     * @param brandId
     * @param themePersistentIds
     * @param contextIds
     * @param exporterConfigurationProperties
     * @param previewMode
     */
    async createExporterBuild(workspaceId, designSystemId, versionId, exporterId, destinations, brandId, themePersistentIds, contextIds, exporterConfigurationProperties, previewMode) {
        return this.dataCore.createExporterBuild(workspaceId, designSystemId, versionId, exporterId, destinations, brandId, themePersistentIds, contextIds, exporterConfigurationProperties, previewMode);
    }
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Delete
    async deletePipeline(workspaceId, pipelineId) {
        return this.dataCore.deletePipeline(workspaceId, pipelineId);
    }
}
